// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import {
  STATUS_PENDING,
} from '../../constants/types';
// feature driver-cards-v2
import {
  MIN_LOAD_DURATION_MS,
  EDIT_COMMAND_MOVE_LOAD,
  LEGACY_TIMELINE_EVENTS_FIELD,
  TIMELINE_EVENT_KIND_DELIVERY,
} from './constants';
//////////////////////////////////////////////////

const TERMINAL_STOP_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-route-builder-stop-locked',
  'Route Builder stop time cannot be changed',
);

const EVENT_STATUS_UNAVAILABLE_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-event-status-unavailable',
  'Event status is unavailable',
);

const EVENT_APPOINTMENT_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-event-has-appointment',
  'Event has an appointment',
);

const EVENT_STATUS_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-event-status-not-pending',
  'Event status is not Pending',
);

const OUTSIDE_PERIOD_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-trip-outside-selected-period',
  'Read-only: trip is outside the selected period',
);

const PICKUP_UNAVAILABLE_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-pickup-event-unavailable',
  'Pickup event is unavailable',
);

const DELIVERY_UNAVAILABLE_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-delivery-event-unavailable',
  'Delivery event is unavailable',
);

const EVENTS_UNAVAILABLE_LOCK_REASON = G.getWindowLocale(
  'validation:driver-cards-v2-events-unavailable',
  'Events are unavailable',
);

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const getEventLockReason = (event) => {
  if (G.isStopTypeTerminal(event && event[GC.FIELD_STOP_TYPE])) return TERMINAL_STOP_LOCK_REASON;

  if (!event || !hasValue(event.status)) return EVENT_STATUS_UNAVAILABLE_LOCK_REASON;

  if ([
    GC.FIELD_LOAD_APPOINTMENT_DATE,
    GC.FIELD_APPOINTMENT_EARLY_DATE,
    GC.FIELD_APPOINTMENT_LATE_DATE,
  ].some(
    (field) => hasValue(event[field]),
  )) return EVENT_APPOINTMENT_LOCK_REASON;

  return event.status === STATUS_PENDING ? '' : EVENT_STATUS_LOCK_REASON;
};

export const getRouteBuilderGuid = G.getTelRouteGuid;
export const hasTerminalStop = G.hasTelTerminalStop;
export const isRouteBuilderLoad = G.isRouteBuilderTel;

const isLoadWithinRange = (load, range) => {
  const endAt = new Date(load.endAt).getTime();
  const startAt = new Date(load.startAt).getTime();

  return hasValue(load.endAt) && hasValue(load.startAt)
    && Number.isFinite(endAt) && Number.isFinite(startAt)
    && Number.isFinite(range.endAt) && Number.isFinite(range.startAt)
    && startAt < endAt && startAt >= range.startAt && endAt <= range.endAt;
};

const hasValidTimelineSequence = (load) => {
  const endAt = new Date(load.endAt).getTime();
  const startAt = new Date(load.startAt).getTime();
  const eventAts = (load.events || []).map(({ at }) => new Date(at).getTime());

  return endAt - startAt >= MIN_LOAD_DURATION_MS
    && eventAts.every((at) => Number.isFinite(at) && at >= startAt && at <= endAt)
    && eventAts.every((at, index) => index === 0 || at >= eventAts[index - 1]);
};

export const getTimelineEditLocks = (load, range) => {
  const source = load.source || {};
  const events = load.events || [];

  if (isRouteBuilderLoad(load)) {
    return {
      readOnly: true,
      end: TERMINAL_STOP_LOCK_REASON,
      move: TERMINAL_STOP_LOCK_REASON,
      start: TERMINAL_STOP_LOCK_REASON,
      events: events.map(() => TERMINAL_STOP_LOCK_REASON),
    };
  }

  if (!isLoadWithinRange(load, range)) {
    return {
      readOnly: true,
      end: OUTSIDE_PERIOD_LOCK_REASON,
      move: OUTSIDE_PERIOD_LOCK_REASON,
      start: OUTSIDE_PERIOD_LOCK_REASON,
      events: events.map(() => OUTSIDE_PERIOD_LOCK_REASON),
    };
  }

  const eventLocks = events.map((event) => getEventLockReason(event.source));
  const firstPickupIndex = events.findIndex(({ kind }) => kind === GC.EVENT_TYPE_PICKUP);

  const finalDeliveryIndex = events.reduce((result, event, index) => (
    event.kind === TIMELINE_EVENT_KIND_DELIVERY ? index : result
  ), -1);

  const rawEvents = [GC.FIELD_LOAD_STOPS, LEGACY_TIMELINE_EVENTS_FIELD, GC.FIELD_STOPS].reduce((events, field) => (
    Array.isArray(source[field]) ? [...events, ...source[field]] : events
  ), []);

  const sourceEventLocks = rawEvents.map(getEventLockReason);

  return {
    events: eventLocks,
    move: [...eventLocks, ...sourceEventLocks].find(Boolean)
      || (!events.length ? EVENTS_UNAVAILABLE_LOCK_REASON : ''),
    start: firstPickupIndex < 0 ? PICKUP_UNAVAILABLE_LOCK_REASON : eventLocks[firstPickupIndex],
    end: finalDeliveryIndex < 0 ? DELIVERY_UNAVAILABLE_LOCK_REASON : eventLocks[finalDeliveryIndex],
  };
};

export const isTimelineEditAllowed = (load, command, range) => {
  if (!load || !command.after) return false;

  const locks = getTimelineEditLocks(load, range);
  const after = command.after;
  const timeChanged = (left, right) => new Date(left).getTime() !== new Date(right).getTime();

  if (locks.readOnly || !isLoadWithinRange(after, range) || !hasValidTimelineSequence(after)) return false;

  if (command.kind === EDIT_COMMAND_MOVE_LOAD && locks.move) return false;

  if (locks.start && timeChanged(load.startAt, after.startAt)) return false;

  if (locks.end && timeChanged(load.endAt, after.endAt)) return false;

  return load.events.every((event, index) => {
    const next = (after.events || []).find(({ id }) => id === event.id);

    return next && (!locks.events[index] || !timeChanged(event.at, next.at));
  });
};
